import {
  Identify,
  identify,
  initAll,
  setUserId,
  track,
} from '@amplitude/unified';
import type { NormalizedAccountInfo, NormalizedUserInfo } from './types';
import { getAmplitudeEnvironmentSuffix } from './utils';

/** Analytics + session replay paths on portal hosts (see amplitude-proxy helm chart). */
const AMPLITUDE_PROXY_PATHS = {
  serverUrl: '/_amplitude/2/httpapi',
  trackServerUrl: '/_amplitude/sessions/v2/track',
  configServerUrl: '/_amplitude/config',
} as const;

/** Guides & Surveys paths on portal hosts (see amplitude-proxy chart v1.1+). */
const AMPLITUDE_ENGAGEMENT_PROXY_PATHS = {
  serverUrl: '/_amplitude/gs',
  cdnUrl: '/_amplitude/cdn',
  mediaUrl: '/_amplitude/media',
} as const;

interface AmplitudeServerUrls {
  serverUrl: string;
  trackServerUrl: string;
  configServerUrl: string;
}

/** Guides & Surveys (engagement) — SDK options are origins; the SDK appends paths. */
interface AmplitudeEngagementProxyUrls {
  serverUrl: string;
  cdnUrl: string;
  mediaUrl: string;
}

export interface AmplitudeConfig {
  apiKey?: string;
  autocapture?: boolean;
  /** Fraction of sessions to record for replay (0–1). Defaults to 0.1 (10%). */
  sessionReplaySampleRate?: number;
  /**
   * Portal host that serves the /_amplitude ingress proxy for this environment
   * (e.g. https://qa.rapidsosportal.com). Required for cross-domain apps such as
   * ia.rapidsos.com; apps on rapidsosportal.com set this to their portal origin.
   */
  proxyOrigin?: string;
}

let activeConfig: AmplitudeConfig | null = null;

// Suffix profileId with the environment so Amplitude segments users per env and
// the id clears Amplitude's default minIdLength (5) for short numeric ids.
const getAmplitudeUserId = (
  profileId: string | number | null | undefined,
): string | undefined => {
  const id = profileId == null ? '' : String(profileId).trim();
  return id
    ? `${id}-${getAmplitudeEnvironmentSuffix(activeConfig?.proxyOrigin)}`
    : undefined;
};

const normalizeOrigin = (origin: string): string => origin.replace(/\/$/, '');

const getAmplitudeProxyUrls = (origin: string): AmplitudeServerUrls => {
  const base = normalizeOrigin(origin);
  return {
    serverUrl: `${base}${AMPLITUDE_PROXY_PATHS.serverUrl}`,
    trackServerUrl: `${base}${AMPLITUDE_PROXY_PATHS.trackServerUrl}`,
    configServerUrl: `${base}${AMPLITUDE_PROXY_PATHS.configServerUrl}`,
  };
};

const getAmplitudeEngagementProxyUrls = (
  origin: string,
): AmplitudeEngagementProxyUrls => {
  const base = normalizeOrigin(origin);
  return {
    serverUrl: `${base}${AMPLITUDE_ENGAGEMENT_PROXY_PATHS.serverUrl}`,
    cdnUrl: `${base}${AMPLITUDE_ENGAGEMENT_PROXY_PATHS.cdnUrl}`,
    mediaUrl: `${base}${AMPLITUDE_ENGAGEMENT_PROXY_PATHS.mediaUrl}`,
  };
};

export const isAmplitudeEnabled = () => !!activeConfig?.apiKey;

type AmplitudeNestedIdentifyProperties = Record<
  string,
  string | number | boolean
>;

type AmplitudeIdentifyPropertyValue =
  | string
  | number
  | boolean
  | AmplitudeNestedIdentifyProperties;

const toAmplitudeNestedProperties = (
  values: Record<string, string | number | boolean | null | undefined>,
): AmplitudeNestedIdentifyProperties =>
  Object.fromEntries(
    Object.entries(values).filter(([, value]) => value != null && value !== ''),
  ) as AmplitudeNestedIdentifyProperties;

/** Maps normalized Gainsight identify payloads to Amplitude user properties. */
export const getAmplitudeIdentifyProperties = (
  userInfo: NormalizedUserInfo,
  accountInfo?: NormalizedAccountInfo,
): Record<string, AmplitudeIdentifyPropertyValue> => {
  const result: Record<string, AmplitudeIdentifyPropertyValue> =
    toAmplitudeNestedProperties(
      Object.fromEntries(
        Object.entries(userInfo).filter(([key]) => key !== 'id'),
      ),
    );

  if (accountInfo) {
    const account = toAmplitudeNestedProperties({ ...accountInfo });
    if (Object.keys(account).length > 0) {
      result.account = account;
    }
  }

  return result;
};

const isSettableIdentifyValue = (
  value: AmplitudeIdentifyPropertyValue | null | undefined,
): value is AmplitudeIdentifyPropertyValue => value != null && value !== '';

export const initializeAmplitude = (
  config: AmplitudeConfig,
  profileId?: string | number | null,
): Promise<void> => {
  if (!config.apiKey || activeConfig) {
    return Promise.resolve();
  }

  activeConfig = config;
  const initialUserId = getAmplitudeUserId(profileId);

  const proxyOrigin = config.proxyOrigin?.trim();
  const proxyUrls = proxyOrigin ? getAmplitudeProxyUrls(proxyOrigin) : null;
  const engagementProxyUrls = proxyOrigin
    ? getAmplitudeEngagementProxyUrls(proxyOrigin)
    : null;

  // initAll is the recommended unified entry point: analytics, session replay,
  // experiment, and guides/surveys. Pass normalized userId in analytics so
  // engagement boots with a valid id instead of a stale short cookie value.
  // autocapture replaces the deprecated defaultTracking option (SDK >= 2.10).
  // The SDK queues track/identify/setUserId until init completes.
  return initAll(config.apiKey, {
    analytics: {
      autocapture: config.autocapture ?? true,
      ...(proxyUrls && {
        serverUrl: proxyUrls.serverUrl,
        // Required when serverUrl points at our proxy rather than api2.amplitude.com.
        enableRequestBodyCompression: true,
        // sr-client-cfg.amplitude.com/config — same upstream as sessionReplay.configServerUrl.
        remoteConfig: {
          serverUrl: proxyUrls.configServerUrl,
        },
      }),
      ...(initialUserId && { userId: initialUserId }),
    },
    sessionReplay: {
      sampleRate: config.sessionReplaySampleRate ?? 0.1,
      ...(proxyUrls && {
        trackServerUrl: proxyUrls.trackServerUrl,
        configServerUrl: proxyUrls.configServerUrl,
      }),
    },
    ...(engagementProxyUrls && {
      engagement: engagementProxyUrls,
    }),
  });
};

export const identifyAmplitudeUser = (
  profileId: string | number | null | undefined,
  userProperties?: Record<
    string,
    AmplitudeIdentifyPropertyValue | null | undefined
  >,
) => {
  const userId = getAmplitudeUserId(profileId);
  if (!userId || !isAmplitudeEnabled()) {
    return;
  }

  setUserId(userId);

  if (!userProperties) {
    return;
  }

  const identifyEvent = new Identify();
  for (const [key, value] of Object.entries(userProperties)) {
    if (isSettableIdentifyValue(value)) {
      identifyEvent.set(key, value);
    }
  }

  identify(identifyEvent);
};

export const trackAmplitudeEvent = (
  eventName: string,
  eventProperties?: Record<string, unknown>,
) => {
  if (!isAmplitudeEnabled()) {
    return;
  }

  track(eventName, eventProperties);
};
